import React from 'react';
import { DollarSignIcon, Code2Icon, CpuIcon, ReceiptIcon, ShieldIcon, LeafIcon } from 'lucide-react';
import { DocsLayout } from '../components/DocsLayout';
const tocItems = [{
  id: 'why-openfpga',
  label: 'Why OpenFPGA?'
}, {
  id: 'price-performance',
  label: 'Price and Performance'
}, {
  id: 'standardized-api',
  label: 'Standardized API'
}, {
  id: 'fpga-models',
  label: 'FPGA-Optimized Models'
}, {
  id: 'billing',
  label: 'Consolidated Billing'
}, {
  id: 'availability',
  label: 'Higher Availability'
}, {
  id: 'efficiency',
  label: 'Superior Efficiency'
}];
interface PrincipleCardProps {
  icon: React.ElementType;
  title: string;
  id: string;
  children: React.ReactNode;
}
function PrincipleCard({
  icon: Icon,
  title,
  id,
  children
}: PrincipleCardProps) {
  return <div id={id} className="scroll-mt-24 rounded-lg border border-gate-border bg-gate-surface/40 p-6 border-l-2 border-l-gate-teal" data-id="element-471">
      <div className="flex items-start gap-4" data-id="element-472">
        <div className="w-10 h-10 rounded-lg bg-gate-teal/10 border border-gate-teal/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-473">
          <Icon className="w-5 h-5 text-gate-teal" data-id="element-474" />
        </div>
        <div data-id="element-475">
          <h3 className="text-lg font-bold text-white mb-2" data-id="element-476">{title}</h3>
          <p className="text-gate-text-secondary leading-relaxed" data-id="element-477">{children}</p>
        </div>
      </div>
    </div>;
}
export function DocsPrinciplesPage() {
  return <DocsLayout activePage="Principles" breadcrumb="Principles" tocItems={tocItems} data-id="element-478">
      {/* Title */}
      <h1 className="text-4xl font-bold text-white mb-2 tracking-tight" data-id="element-479">
        Principles
      </h1>
      <p className="text-lg text-gate-text-secondary mb-10" data-id="element-480">
        Our approach to FPGA-accelerated inference
      </p>

      {/* Intro */}
      <p className="text-gate-text-secondary leading-relaxed mb-12" data-id="element-481">
        OpenFPGA helps developers optimize AI inference through FPGA
        acceleration. We believe the future is efficient, cost-effective, and
        hardware-optimized.
      </p>

      {/* Why OpenFPGA */}
      <h2 id="why-openfpga" className="text-2xl font-bold text-white mb-6 scroll-mt-24" data-id="element-482">
        Why OpenFPGA?
      </h2>

      <div className="space-y-4 mb-14" data-id="element-483">
        <PrincipleCard icon={DollarSignIcon} title="Price and Performance" id="price-performance" data-id="element-484">
          OpenFPGA delivers superior inference economics through FPGA
          efficiency—achieving faster inference at lower cost than traditional
          GPU infrastructure. Our synthesis optimizations ensure you get the
          best price-performance ratio for your models.
        </PrincipleCard>

        <PrincipleCard icon={Code2Icon} title="Standardized API" id="standardized-api" data-id="element-485">
          OpenAI-compatible API means no code changes required. Seamlessly
          switch between FPGA-accelerated models and maintain compatibility with
          existing tools and workflows. Deploy once, optimize everywhere.
        </PrincipleCard>

        <PrincipleCard icon={CpuIcon} title="FPGA-Optimized Models" id="fpga-models" data-id="element-486">
          We automatically synthesize and optimize LLMs to run efficiently on
          FPGA architecture. Our compilation layer handles the complexity of
          hardware mapping, delivering inference speeds comparable to GPUs at a
          fraction of the cost.
        </PrincipleCard>

        <PrincipleCard icon={ReceiptIcon} title="Consolidated Billing" id="billing" data-id="element-487">
          Simple, transparent usage-based pricing. Pay only for the inference
          you use, with built-in cost savings from FPGA efficiency—typically
          40-60% less than equivalent GPU inference.
        </PrincipleCard>

        <PrincipleCard icon={ShieldIcon} title="Higher Availability" id="availability" data-id="element-488">
          FPGA architecture provides inherent reliability with lower thermal
          constraints and power requirements. Intelligent routing across our
          FPGA cluster ensures your requests complete even under high load.
        </PrincipleCard>

        <PrincipleCard icon={LeafIcon} title="Superior Efficiency" id="efficiency" data-id="element-489">
          FPGAs deliver better performance-per-watt and performance-per-dollar
          than GPUs for inference workloads. Lower power consumption means lower
          costs and more sustainable AI infrastructure at scale.
        </PrincipleCard>
      </div>
    </DocsLayout>;
}